import { useEffect, useState } from "react";
import { AlertCircle } from "lucide-react";
import { Modal } from "../../components/ui/modal";
import { Dropdown } from "../../components/ui/dropdown";
import { ApiError } from "../../lib/api";
import { CustomFieldInput } from "./custom-field-input";
import { LEAD_SOURCES, type Lead, type LeadCampaign } from "./types";

/** The lead already using this phone number or email, as reported by the backend. */
type DuplicateLead = { id: string; name: string; contactId: string };
type DuplicateWarning = { field: "phone" | "email"; message: string; lead: DuplicateLead };

interface LeadFormModalProps {
  campaigns: LeadCampaign[];
  onClose: () => void;
  onSave: (data: Omit<Lead, "id" | "fresh" | "createdAt"> & { allowDuplicate?: boolean }) => Promise<void>;
  /** Opens the clashing lead so the agent can check it before deciding. */
  onViewExisting?: (leadId: string) => void;
}

const fieldCls =
  "w-full rounded-lg border border-line bg-transparent px-3 py-2 text-sm text-ink outline-none focus:border-brand";
const labelCls = "mb-1.5 block text-xs font-semibold text-ink-2";

export function LeadFormModal({ campaigns, onClose, onSave, onViewExisting }: LeadFormModalProps) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [campaignId, setCampaignId] = useState("");
  const [notes, setNotes] = useState("");
  const [customValues, setCustomValues] = useState<Record<string, string>>({});
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [duplicate, setDuplicate] = useState<DuplicateWarning | null>(null);

  // A different campaign can have entirely different questions, so answers
  // to the previous one's don't carry over.
  useEffect(() => { setCustomValues({}); }, [campaignId]);

  // Name and Phone number are always shown; a campaign can still relabel
  // them or make them optional (see campaign-modal.tsx), so only their
  // required flag and label are read from the selected campaign.
  const selectedCampaign = campaigns.find((c) => c.id === campaignId);
  const nameField = selectedCampaign?.fields?.find((f) => f.id === "name");
  const phoneField = selectedCampaign?.fields?.find((f) => f.id === "phone");
  const nameRequired = nameField ? !!nameField.required : true;
  const phoneRequired = phoneField ? !!phoneField.required : true;
  // The campaign can also define its own Email question (a custom field
  // stored under `values`) — separate from the generic Email box below,
  // which only writes to the lead's top-level email. Without this, filling
  // the box still left that campaign's own question unanswered, so the
  // backend rejected the save as "Email is required" even though the
  // visible Email field was filled in.
  const emailField = selectedCampaign?.fields?.find((f) => f.id !== "name" && f.id !== "phone" && f.kind === "email");
  // Every other question this campaign asks — the Email box above covers
  // that one by kind when present, so this list excludes it to avoid asking
  // for the same answer twice.
  const customFields = (selectedCampaign?.fields ?? []).filter(
    (f) => f.id !== "name" && f.id !== "phone" && f !== emailField,
  );

  const submit = async ({ allowDuplicate = false } = {}) => {
    if (saving) return;
    if (nameField && nameRequired && !name.trim()) {
      setError(`${nameField.label} is required.`);
      return;
    }
    if (phoneField && phoneRequired && !phone.trim()) {
      setError(`${phoneField.label} is required.`);
      return;
    }
    const unanswered = customFields.find((f) => f.required && !(customValues[f.id] || "").trim());
    if (unanswered) {
      setError(`${unanswered.label} is required.`);
      return;
    }
    if (!campaignId) {
      setError("Pick a lead campaign (or create one first).");
      return;
    }
    setSaving(true); setError(""); setDuplicate(null);
    try {
      const values: Record<string, string> = { ...customValues };
      if (emailField && email.trim()) values[emailField.id] = email.trim();
      // Awaited: the backend can come back with a duplicate-phone warning, and
      // the modal needs to stay open showing it instead of closing as if saved.
      await onSave({
        name: name.trim() || undefined,
        phone: phone.trim() || undefined,
        email: email.trim() || undefined,
        campaignId,
        source: LEAD_SOURCES[0], // fallback default since UI was removed
        notes: notes.trim() || undefined,
        ...(Object.keys(values).length ? { values } : {}),
        ...(allowDuplicate ? { allowDuplicate: true } : {}),
      });
      onClose();
    } catch (e) {
      // A repeated phone or email isn't a failure — it's a warning the agent
      // can act on, so it gets its own panel rather than the red error line.
      if (e instanceof ApiError && e.status === 409 && (e.data?.code === "duplicate_phone" || e.data?.code === "duplicate_email")) {
        setDuplicate({ field: e.data.code === "duplicate_phone" ? "phone" : "email", message: e.data.message, lead: e.data.lead as DuplicateLead });
      } else {
        setError(e instanceof Error ? e.message : "Unable to add customer.");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title="New customer"
      subtitle="Required fields depend on the selected campaign's form — everything else can be filled in later."
      onClose={onClose}
      width="max-w-lg"
      footer={
        <>
          <button onClick={onClose} className="rounded-lg px-4 py-2 text-sm font-semibold text-ink-2 hover:bg-soft">
            Cancel
          </button>
          <button disabled={saving} onClick={() => void submit()} className="rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-hover disabled:opacity-50">
            {saving ? "Adding…" : "Add customer"}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <div>
          <label className={labelCls}>Campaign</label>
          {campaigns.length === 0 ? (
            <p className="rounded-lg border border-dashed border-line px-3 py-2 text-xs text-ink-3">
              No campaigns yet — create one first.
            </p>
          ) : (
            <Dropdown
              value={campaignId}
              options={[
                { value: "", label: "Select a campaign" },
                ...campaigns.map((c) => ({ value: c.id, label: c.name })),
              ]}
              onChange={setCampaignId}
              ariaLabel="Campaign"
              searchable
            />
          )}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>{nameField?.label ?? "Full name"}{nameRequired ? " *" : ""}</label>
            <input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Sunita Mehta" className={fieldCls} />
          </div>
          <div>
            <label className={labelCls}>{phoneField?.label ?? "Phone number"}{phoneRequired ? " *" : ""}</label>
            <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="10-digit mobile" className={fieldCls} />
          </div>
        </div>

        <div>
          <label className={labelCls}>Email</label>
          <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@company.in" className={fieldCls} />
        </div>

        {customFields.length > 0 && (
          // Stacked, not paired 2-up like Name/Phone — question text is
          // arbitrary length and wraps, so a side-by-side row would leave
          // the shorter question's input misaligned with its neighbor's.
          <div className="space-y-4">
            {customFields.map((f) => (
              <div key={f.id}>
                <label className={labelCls}>{f.label}{f.required ? " *" : ""}</label>
                <CustomFieldInput
                  field={f}
                  value={customValues[f.id] ?? ""}
                  onChange={(v) => setCustomValues((prev) => ({ ...prev, [f.id]: v }))}
                  inputClassName={fieldCls}
                />
              </div>
            ))}
          </div>
        )}

        <div>
          <label className={labelCls}>Notes</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Anything useful for the next call..."
            rows={3}
            className={fieldCls + " resize-none"}
          />
        </div>

        {error && <p className="text-xs font-semibold text-red-500">{error}</p>}

        {duplicate && (
          <div className="rounded-xl border border-line bg-soft p-4">
            <p className="flex items-center gap-2 text-sm font-semibold text-ink">
              <AlertCircle className="h-4 w-4 text-red-500" /> {duplicate.field === "phone" ? "Phone number" : "Email"} already exists
            </p>
            <p className="mt-1.5 text-sm text-ink-2">{duplicate.message}</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {onViewExisting && (
                <button
                  type="button"
                  onClick={() => onViewExisting(duplicate.lead.id)}
                  className="rounded-full border border-line bg-elevated px-3.5 py-1.5 text-xs font-semibold text-ink hover:bg-soft"
                >
                  View existing contact
                </button>
              )}
              <button
                type="button"
                disabled={saving}
                onClick={() => void submit({ allowDuplicate: true })}
                className="rounded-full border border-line bg-elevated px-3.5 py-1.5 text-xs font-semibold text-ink hover:bg-soft disabled:opacity-50"
              >
                {saving ? "Adding…" : "Add anyway"}
              </button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
